// The phone's engines and the messages between the console's tabs and the phone (docs/ringcentral/SOFTPHONE.md).
// No DOM here: this runs in the SharedWorker (phone-worker.ts) as well as in a page.
//
//   simulator   : sim-engine.ts — the server's simulated phone, driven through /api/softphone/sim/*
//   ringcentral : rc-engine.ts — the Web Phone SDK (decision D4), SIP in the worker, WebRTC in a tab (media-host.ts)
//
// One engine per browser: the hub (hub.ts) owns it, every tab shows the same state and sends commands to it. The worker
// holds no credentials. Every server call goes through a tab (EngineHost.http), with the tab's own sign-in.

export type CallState = 'ringing' | 'connected' | 'held' | 'ended';

export interface PhoneCall {
  /** The engine's id for the call (the simulator: its session id). */
  id: string;
  /** The provider's telephony session: what the server's records are keyed by. */
  telephonySessionId: string | null;
  direction: 'inbound' | 'outbound';
  state: CallState;
  /** The other party's number as the phone shows it (E.164); null when the phone doesn't know it yet. */
  remote: string | null;
  /** The SOC line the call is on (the caller ID shown on an outbound call). */
  line: string | null;
  muted: boolean;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  /** On a warm transfer's consultation call: the id of the call being transferred. */
  consultFor: string | null;
}

export type Registration = 'off' | 'starting' | 'registered' | 'reconnecting' | 'needs_sign_in' | 'error';

export interface PhoneState {
  engine: 'simulator' | 'ringcentral' | null;
  simulated: boolean;
  registration: Registration;
  error: string | null;
  calls: PhoneCall[];
  /** The tab whose page plays the call's audio (Web Phone: WebRTC lives in a page, not the worker). */
  audioTab: string | null;
  /** Tabs connected to the phone. */
  tabs: number;
}

export const IDLE: PhoneState = { engine: null, simulated: false, registration: 'off', error: null, calls: [], audioTab: null, tabs: 0 };

export type ControlAction =
  | 'answer'
  | 'decline'
  | 'to_voicemail'
  | 'hangup'
  | 'mute'
  | 'unmute'
  | 'hold'
  | 'unhold'
  | 'dtmf'
  | 'transfer'
  | 'warm_transfer_start'
  | 'warm_transfer_complete'
  | 'warm_transfer_cancel';

export interface ControlArgs {
  to?: string;
  digits?: string;
  reason?: string;
}

/** What the engine needs from the page: the console's API, with its sign-in, and a page to run a call's WebRTC in. */
export interface EngineHost {
  http<T = unknown>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T>;
  /**
   * A WebRTC operation in a tab (media-host.ts): on `preferTab` while it is connected, else on the tab in use. Resolves
   * with the result and the tab that ran it (a different tab means the call's media moved and must be renegotiated).
   */
  media(op: MediaOp, args: MediaArgs, preferTab: string | null): Promise<{ data: unknown; tab: string }>;
}

export type MediaOp = 'createOffer' | 'createAnswer' | 'applyAnswer' | 'changeInputDevice' | 'changeOutputDevice' | 'setMuted' | 'sendDtmf' | 'dispose';

/** Every media operation carries the call's context, so a tab that doesn't have the call yet can set it up. */
export interface MediaArgs {
  callId: string;
  direction: 'inbound' | 'outbound';
  stunServers: string[];
  iceRestart?: boolean;
  sdp?: string;
  deviceId?: string;
  muted?: boolean;
  tones?: string;
  duration?: number;
  interToneGap?: number;
}

export interface PhoneEngine {
  readonly name: 'simulator' | 'ringcentral';
  readonly simulated: boolean;
  /**
   * True when the server records each control itself (the simulator's routes act and record). Otherwise the hub reports
   * every control and its outcome to POST /api/softphone/control after the engine acts.
   */
  readonly recordsOwnControls: boolean;
  start(): Promise<void>;
  stop(): Promise<void>;
  /** The network came back (or a tab says the phone looks stale): reconnect and reread the calls. */
  recover(): Promise<void>;
  dial(to: string, callerId: string | null): Promise<PhoneCall | null>;
  /** Acts on a call; resolves when the provider has done it, rejects when it couldn't. */
  control(callId: string, action: ControlAction, args: ControlArgs): Promise<{ consultCallId?: string }>;
  /** A 'softphone' message from the console's realtime stream (the simulator's calls changing). */
  serverMessage(data: unknown): void;
  calls(): PhoneCall[];
  onChange(cb: () => void): void;
  /** The engine's own view of its registration (lost and regained connections); the hub shows it. */
  onStatus?(cb: (registration: Registration, error: string | null) => void): void;
  /** The connected tabs changed: a call whose media was in a tab that went is moved to another (re-INVITE). */
  tabsChanged?(tabIds: string[]): void;
}

// ───────── messages between a tab and the hub ─────────

export type TabMessage =
  | { t: 'hello'; tabId: string; engine: 'simulator' | 'ringcentral'; visible: boolean }
  | { t: 'media_result'; id: string; ok: boolean; data?: unknown; error?: string }
  | { t: 'cmd'; id: string; op: 'start' | 'stop' | 'recover' | 'dial' | 'control'; args?: Record<string, unknown> }
  | { t: 'http_result'; id: string; ok: boolean; data?: unknown; error?: string; status?: number }
  | { t: 'server'; data: unknown }
  | { t: 'active'; tabId: string }
  | { t: 'ping'; tabId: string }
  | { t: 'bye'; tabId: string };

export type HubMessage =
  | { t: 'state'; state: PhoneState }
  | { t: 'result'; id: string; ok: boolean; data?: unknown; error?: string }
  | { t: 'http'; id: string; method: 'GET' | 'POST'; path: string; body?: unknown }
  | { t: 'media'; id: string; op: MediaOp; args: MediaArgs };

/** A port to a tab: a SharedWorker MessagePort, or a MessageChannel end when there is no SharedWorker. */
export interface PortLike {
  postMessage(m: unknown): void;
  onmessage: ((e: { data: unknown }) => void) | null;
  start?(): void;
}

/** Only these paths are proxied for the phone, whatever asks. */
export function phonePathAllowed(path: string): boolean {
  return /^\/api\/softphone(\/|$)/.test(path) && !path.includes('..');
}

/** Valid DTMF: 0-9, *, #, A-D. */
export const DTMF = /^[0-9*#A-D]{1,32}$/;

export function liveCalls(s: PhoneState): PhoneCall[] {
  return s.calls.filter((c) => c.state !== 'ended');
}
